import { Download } from "lucide-react";
import { Seo, Eyebrow } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { COMPANY } from "@/data/site";

const BOILERPLATE = `DataFrontier is a research lab building memory as infrastructure for AI — sovereign, verifiable, and local-first. Its research spans UPII (Unified Personal Intelligence Infrastructure), whose open-source core is live on PyPI, and UEII (Unified Enterprise Intelligence Infrastructure), which adds governance, PII membranes and audit for organizations. The work ships as Smritium, the sovereign memory layer for AI. DataFrontier Innovations Private Limited is based in Bengaluru, India (CIN ${"U62091KA2024PTC184641"}).`;

const PALETTE = [
  { n: "Ink", hex: "#0A0A0B", cls: "bg-ink border border-white/15" },
  { n: "Bone", hex: "#F5F2EA", cls: "bg-bone" },
  { n: "Smriti Saffron", hex: "#E8722A", cls: "bg-saffron" },
  { n: "Slate", hex: "#8A8F98", cls: "bg-mist" },
  { n: "Signal Green", hex: "#3DBB7E", cls: "bg-signal" },
];

const ASSETS = [
  { n: "Wordmark — dark", f: "/brand/lockup-dark.webp", d: "Lockup for light backgrounds" },
  { n: "Wordmark — white", f: "/brand/lockup-white.webp", d: "Lockup for dark backgrounds" },
  { n: "Mark — dark", f: "/brand/mark-dark.png", d: "Standalone mark, light backgrounds" },
  { n: "Mark — white", f: "/brand/mark-white.png", d: "Standalone mark, dark backgrounds" },
];

export default function Press() {
  const copy = () => {
    navigator.clipboard?.writeText(BOILERPLATE);
  };
  return (
    <div data-testid="press-page">
      <Seo title="Press" description="Press resources, brand assets and company boilerplate for DataFrontier." />
      <PageHero
        eyebrow="Company · Press"
        title="Press & brand."
        lead="Boilerplate, logos, palette and type. For interviews and enquiries, write to press@datafrontier.co."
      />

      <Section>
        <Reveal>
          <Eyebrow>Company boilerplate</Eyebrow>
          <div className="mt-6 border border-white/10 bg-white/[0.015] p-8">
            <p className="max-w-3xl font-serif text-lg leading-relaxed text-bone/90">{BOILERPLATE}</p>
            <button onClick={copy} data-testid="copy-boilerplate" className="mt-6 border border-white/15 px-4 py-2 font-mono text-xs text-mist transition-colors hover:border-saffron/40 hover:text-bone">
              Copy boilerplate
            </button>
          </div>
        </Reveal>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>Logo assets</Eyebrow>
        </Reveal>
        <Stagger className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {ASSETS.map((a) => (
            <Item key={a.n} className="flex flex-col bg-ink">
              <div className={`flex h-32 items-center justify-center ${a.f.includes("white") ? "bg-ink-800" : "bg-bone"}`}>
                <img src={a.f} alt={a.n} className={a.f.includes("mark") ? "h-14 w-auto" : "h-6 w-auto"} />
              </div>
              <div className="flex items-center justify-between p-5">
                <div>
                  <div className="text-sm text-bone">{a.n}</div>
                  <div className="mt-1 text-xs text-mist">{a.d}</div>
                </div>
                <a href={a.f} download className="text-mist hover:text-saffron" aria-label={`Download ${a.n}`}>
                  <Download className="h-4 w-4" />
                </a>
              </div>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Palette</Eyebrow>
            <div className="mt-8 space-y-4">
              {PALETTE.map((p) => (
                <div key={p.n} className="flex items-center gap-4">
                  <span className={`h-10 w-10 ${p.cls}`} />
                  <div className="flex-1">
                    <div className="text-sm text-bone">{p.n}</div>
                    <div className="font-mono text-xs text-mist">{p.hex}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-sm text-xs leading-relaxed text-mist">
              Never more than one accent per viewport. Saffron is used sparingly — links, active states, the mark.
              Whitespace is the second brand color.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <Eyebrow>Type</Eyebrow>
            <div className="mt-8 space-y-8">
              <div>
                <div className="font-mono text-xs text-mist">Serif — headlines & prose</div>
                <div className="mt-2 font-serif text-4xl text-bone">Fraunces</div>
              </div>
              <div>
                <div className="font-mono text-xs text-mist">Sans — UI & navigation</div>
                <div className="mt-2 font-sans text-4xl text-bone">Inter</div>
              </div>
              <div>
                <div className="font-mono text-xs text-mist">Mono — code, data, citations</div>
                <div className="mt-2 font-mono text-3xl text-bone">JetBrains Mono</div>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
